import { Dispatch, SetStateAction, useState, useEffect, MouseEvent, ReactNode } from "react"
import DateRange from "./FilterBarComponents/DateRange";
import Tags from "./FilterBarComponents/Tags";
import Users from "./FilterBarComponents/Users";
import dayjs, { Dayjs } from "dayjs";
import { ReactComponent as ClearIcon } from '../../assets/Icons/adornments/Close.svg';

export interface FilterBarType {
    label: string,
    param: string,
    type: 'date-range' | 'date' | 'users' | 'tags' | 'multi-select',
    isDefault: boolean,
    groupKey?: string,
    options?: string[],
    icon?: ReactNode,
}

export default function FilterBar({
    filters,
    params,
    setParams,
}: {
    filters: FilterBarType[],
    params: { [key: string]: any | null },
    setParams: Dispatch<SetStateAction<{ [key: string]: any | null }>>
}) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleOpenMenu = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setAnchorEl((prevEl) => prevEl && prevEl.id === event.currentTarget.id ? null : event.currentTarget)
    };

    
    const handleCloseMenu = () => {
        setAnchorEl(null);
    };


    const [ shownFilters, setShownFilters ] = useState<{
        filters: FilterBarType[],
        groupKey?: string,
        value: string | string[] | { [key: string]: Dayjs | null } | null,
    }[]>([]);

    useEffect(() => {
        if (filters.length > 0) {
            let barFilters: {
                filters: FilterBarType[],
                groupKey?: string,
                value: string | string[] | { [key: string]: Dayjs | null } | null,
                icon?: ReactNode,
            }[] = [];

            const paramKeys = Object.keys(params);

            filters.forEach(filter => {
                if (filter.isDefault) {
                    if (filter.groupKey === undefined) {
                        const matchingParam = paramKeys.find(param => param === filter.param);

                        let value: string | string[] | null = null;

                        if (matchingParam) {
                            const paramValue = params[matchingParam as keyof object] as string | null;
                            if (paramValue !== null) {
                                let parsedValue = '';

                                if (filter.type === 'tags') {
                                    parsedValue = JSON.parse(paramValue);
                                } else {
                                    parsedValue = paramValue;
                                }

                                if (typeof parsedValue === 'string' || filter.type !== 'tags') {
                                    value = parsedValue;
                                } else {
                                    value = (parsedValue as string[]).flat(Infinity);
                                }
                            }
                        };

                        barFilters.push({
                            filters: [filter],
                            value,
                            icon: filter.icon,
                        })
                    } else {
                        const existingFilter = barFilters.find(bar => bar.groupKey !== undefined && bar.groupKey === filter.groupKey);
                        
                        const matchingParam = paramKeys.find(param => param === filter.param);
                        let value: string | string[] | { [key: string]: Dayjs | null } | null = null;
                        
                        if (existingFilter) {
                            const existingValue = existingFilter.value;

                            if (matchingParam) {
                                const paramValue = params[matchingParam as keyof object] as string | null;
                                if (filter.type === 'date-range') {
                                    value = {
                                        end: matchingParam.includes('end') || matchingParam.includes('lte') ? paramValue !== null ? dayjs(paramValue) : null : (existingValue as any).end,
                                        start: matchingParam.includes('start') || matchingParam.includes('gte') ? paramValue !== null ? dayjs(paramValue) : null : (existingValue as any).start,
                                    }
                                }
                            }
                            
                            barFilters = barFilters.map(bar => {
                                if (bar.groupKey && bar.groupKey === existingFilter.groupKey) {
                                    return {
                                        ...bar,
                                        filters: bar.filters.concat(filter),
                                        value,
                                    }
                                } else {
                                    return {...bar}
                                }
                            })
                        } else {

                            if (matchingParam) {
                                const paramValue = params[matchingParam as keyof object] as string | null;
                                if (filter.type === 'date-range') {
                                    if (paramValue !== null) {
                                        value = {
                                            end: matchingParam.includes('end') || matchingParam.includes('lte') ? dayjs(paramValue) : null,
                                            start: matchingParam.includes('start') || matchingParam.includes('gte') ? dayjs(paramValue) : null,
                                        }
                                    } else {
                                        value = {
                                            end: null,
                                            start: null,
                                        }
                                    }
                                } else {
                                    value = paramValue;
                                }
                            }

                            barFilters.push({
                                filters: [filter],
                                groupKey: filter.groupKey,
                                value,
                                icon: filter.icon,
                            })
                        }
                    }
                }
            });

            setShownFilters(barFilters);
        }
    }, [filters, params]);


    const applyFilters = (appliedFilters: {
        filters: FilterBarType[],
        groupKey?: string,
        value: string | string[] | { [key: string]: Dayjs | null } | null,
    }[]) => {
        let filterParams: any = {};

        const tagFilter = appliedFilters.find(fil => fil.filters[0].type === 'tags');
        const dateRangeFilters = appliedFilters.filter(fil => fil.filters[0].type === 'date-range');
        const remainingFilters = appliedFilters.filter(fil => fil.filters[0].type !== 'tags' && fil.filters[0].type !== 'date-range');

        let tags: string[][] = [];

        if (tagFilter) {
            const tagParam = tagFilter.filters[0].param;
            if (tagFilter.value !== null) {
                (tagFilter.value as string[]).forEach(val => {
                    tags.push([val]);
                });
            };

            filterParams[tagParam] = tags.length > 0 ? JSON.stringify(tags) : null;

        };

        if (dateRangeFilters) {
            dateRangeFilters.forEach(dateRangeFilter => {
                const dateValues = dateRangeFilter.value as {
                    start: Dayjs | null,
                    end: Dayjs | null,
                } | null;

                const dateFilters = dateRangeFilter.filters;

                dateFilters.forEach(dateFil => {
                    const dateParam = dateFil.param;

                    if (dateValues !== null) {
                        let matchingKey = '';
                        if (dateParam.includes('start') || dateParam.includes('gte')) matchingKey = 'start';
                        if (dateParam.includes('end') || dateParam.includes('lte')) matchingKey = 'end';

                        if (matchingKey) {
                            const dateVal = dateValues[matchingKey as keyof object];
                            filterParams[dateParam] = dateVal !== null ? (dateVal as Dayjs).format('YYYY-MM-DDTHH:mm:ss') : null;
                        };
                    } else {
                        filterParams[dateParam] = null;
                    }
                });
            })
        };

        if (remainingFilters.length > 0) {
            remainingFilters.forEach(filter => {
                const filParam = filter.filters[0].param;
                const filVal = filter.value as string | string[] | null;
                filterParams[filParam] = filVal;
            });
        };


        const updatedParams = {
            ...params,
            ...filterParams,
        };

        setParams(updatedParams);

    };

    const setFilterValue = (filters: FilterBarType[], value: string | string[] | { [key: string]: Dayjs | null } | null) => {
        const updatedFilters = shownFilters.map(filter => {
            if (filter.filters[0].param === filters[0].param) {
                return {
                    ...filter,
                    value,
                }
            } else {
                return {...filter}
            }
        });
        
        setShownFilters(updatedFilters);

        applyFilters(updatedFilters);

    };

    const showClearButton = (filterParams: { [key: string]: string | null }, originalFilters: FilterBarType[]) => {
        if (originalFilters.length > 0) {
            const filterableParams = originalFilters.map(filter => filter.param);
            let filterValues: any[] = [];

            Object.keys(filterParams).forEach(paramKey => {
                const paramValue = filterParams[paramKey];
                if (paramValue !== null && filterableParams.includes(paramKey)) {
                    filterValues.push(paramValue);
                }
            });
    
            if (filterValues.length > 0) {
                return true;
            } else {
                return false;
            }
        }
        
    };

    const clearAllFilters = (e:MouseEvent<HTMLButtonElement>, filterParams: { [key: string]: string | null }, originalFilters: FilterBarType[]) => {
        e.preventDefault();
        
        if (originalFilters.length > 0) {
            let clearedParams: any = {};

            Object.keys(filterParams).forEach(filterParamKey => {
                const matchingFilter = originalFilters.find(fil => fil.param === filterParamKey);
                if (matchingFilter) {
                    clearedParams[filterParamKey] = null;
                }
            });

            const updatedParams = {
                ...params,
                ...clearedParams,
            };

            setParams(updatedParams);
        }
    }

    return (
        <div className="flex justify-start items-center flex-wrap gap-4">
            <>
            {shownFilters.length > 0 && shownFilters.map((filter,i) => {
                const filterType = filter.filters[0].type;
                if (filterType === 'date-range') {
                    return (
                        <DateRange 
                            key={i}
                            filter={filter}
                            setFilterValue={setFilterValue}
                            anchorEl={anchorEl}
                            handleClose={handleCloseMenu}
                            handleOpen={handleOpenMenu}
                        />
                    )
                } else if (filterType === 'tags' || filterType === 'multi-select') {
                    return (
                        <Tags 
                            key={i}
                            filter={filter} 
                            setFilterValue={setFilterValue}
                            anchorEl={anchorEl}
                            handleClose={handleCloseMenu}
                            handleOpen={handleOpenMenu}
                        />
                    )
                } else if (filterType === 'users') {
                    return (
                        <Users 
                            key={i}
                            filter={filter} 
                            setFilterValue={setFilterValue}
                            anchorEl={anchorEl}
                            handleClose={handleCloseMenu}
                            handleOpen={handleOpenMenu}
                        />
                    )
                } else {
                    return null;
                }
            })}
            {showClearButton(params, filters) && (
                <button onClick={(e: MouseEvent<HTMLButtonElement>) => clearAllFilters(e, params, filters)} className="flex justify-start items-center gap-1 text-default hover:text-primary transition-all">
                    <div className="w-[16px]"><ClearIcon /></div>
                    <p className="text-[12px]">Clear All Filters</p>
                </button>
            )}
            </>
        </div>
    )
}